import Package from '@/common/models/package/Package';
import ErrorHandler from '@/ErrorHandler';
import API from '@/exch-app/api';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import {
  createApiAction,
  createAuditAction,
  createDeleteAction,
  createGetAllAction,
  createGetOneAction,
  createPostAction,
  createPutAction,
} from '@/utils/ActionsUtils';
import LocalCache from '@/utils/LocalCache';
import NumbroUtils from '@/utils/NumbroUtils';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

const actions = {
  //
  // EXCHANGE
  //
  setExchange({ commit }, exchange) {
    commit(types.SET_EXCHANGE, exchange);
    if (exchange === null) {
      LocalCache.remove('exchange');
      commit(types.SET_EXCHANGE_SETTINGS, null);
      LocalCache.remove('exchangeSettings');
    } else {
      LocalCache.set('exchange', exchange != null ? JSON.stringify(exchange.toApi()) : null);
    }
  },

  async getPermissions({ commit }, { showLoader = false } = {}) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const permissions = await API.instance.getPermissions().catch((err) => {
      const msg = i18n.global.t('messages.failedToGetPermissions');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    commit(types.SET_PERMISSIONS, permissions);
    LocalCache.set('permissions', permissions.toApi());

    return permissions;
  },

  async clearPermissions({ commit }) {
    commit(types.SET_PERMISSIONS, null);
    LocalCache.remove('permissions');
  },

  async updatePermissions({ commit }, permissions) {
    commit(types.SET_PERMISSIONS, permissions);
    LocalCache.set('permissions', permissions.toApi());
  },

  async getExchangeSettings({ commit }) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    let exchangeSettings;
    try {
      exchangeSettings = await API.instance.getExchangeSettings();
    } catch (err) {
      const msg = i18n.global.t('messages.failedToGetExchangeSettings');
      ErrorHandler.showError(msg, err);
      throw err;
    } finally {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    commit(types.SET_EXCHANGE_SETTINGS, exchangeSettings);
    LocalCache.set('exchangeSettings', exchangeSettings.toApi());

    NumbroUtils.setCurrencySymbol(exchangeSettings.currencySymbol);

    return exchangeSettings;
  },

  async getSuppliers({ commit }, { queryParams, showLoader = false } = {}) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const suppliers = await API.instance.getSuppliers({ queryParams }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetSuppliers');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    commit(types.SET_SUPPLIERS, suppliers);

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return suppliers;
  },

  //
  // AVAILS
  //
  async getAvails({ commit }, { params, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const avails = await API.instance.getAvails(params).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetAvails');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    commit(types.SET_AVAILS, avails);

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return avails;
  },

  getAvailsByUnitPost({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getAvailsByUnitPost(
        params,
        (result) => {
          resolve(result);
        },
        (error) => {
          // Do not show a notification if we are getting detailed avails because
          // we'll try to get non-detailed avails.
          if (params.get_detailed !== undefined && params.get_detailed === false) {
            const msg = i18n.global.t('messages.failedToGetAvails');
            ErrorHandler.showError(msg, error);
          }
          reject();
        },
      );
    });
  },

  getAvailsByUnit({ commit }, lineItemId) {
    return new Promise((resolve, reject) => {
      API.instance.getAvailsByUnit(
        lineItemId,
        (result) => {
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetAvails');
          ErrorHandler.showError(msg, error);
          reject();
        },
      );
    });
  },

  getAvailsExecutionById({ commit }, availsExecutionId) {
    return new Promise((resolve, reject) => {
      API.instance.getAvailsExecutionById(
        availsExecutionId,
        (result) => {
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetAvails');
          ErrorHandler.showError(msg, error);
          reject();
        },
      );
    });
  },

  //
  // DEMOGRAPHICS
  //
  getDemographics: createGetAllAction(
    API.instance.getDemographics,
    types.SET_DEMOGRAPHICS,
    'messages.failedToGetDemographics',
  ),

  //
  // GEOFENCE GROUPS
  //
  // getGeofenceGroups: createGetAllAction(API.instance.getGeofenceGroups, types.SET_GEOFENCE_GROUPS, 'messages.failedToGetGeofenceGroups'),
  async getGeofenceGroups({ commit }, { queryParams, exclusion = false, cancelTokenSource = null, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const geofences = await API.instance.getGeofenceGroups({ queryParams, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetGeofenceGroups');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (exclusion) {
      commit(types.SET_GEOFENCE_GROUPS_EXCLUDED, geofences.items);
    } else {
      commit(types.SET_GEOFENCE_GROUPS, geofences.items);
    }

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return geofences;
  },

  //
  // LANGUAGES
  //
  getLanguages: createGetAllAction(
    API.instance.getLanguages,
    types.SET_LANGUAGES,
    'messages.failedToGetLanguages',
    true,
  ),

  //
  // LOCATIONS
  //
  getLocationsByLevel: createApiAction(API.instance.getLocationsByLevel, null, 'messages.failedToGetLocations', false),

  //
  // TAGS
  //
  getTags: createApiAction(API.instance.getTags, null, 'messages.failedToGetLocations', false),

  async getLocations({ commit }, { queryParams, exclusion = false, cancelTokenSource = null, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const locations = await API.instance.getLocations({ queryParams, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetLocations');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (exclusion) {
      commit(types.SET_LOCATIONS_EXCLUDED, locations.items);
    } else {
      commit(types.SET_LOCATIONS, locations.items);
    }

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return locations;
  },

  async getChildren(
    _,
    { locationID, networkIDs = [], mediaOwnerIDs = [], cancelTokenSource = null, showLoader = true },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const locations = await API.instance
      .children(locationID, networkIDs, mediaOwnerIDs, cancelTokenSource)
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToGetLocations');
        ErrorHandler.showError(msg, err);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return locations;
  },

  getLocationRoots: createApiAction(API.instance.getLocationRoots, null, 'messages.failedToGetLocations', false),

  async treeSearch(
    _,
    { searchTerm, countryIDs = [], networkIDs = [], mediaOwnerIDs = [], cancelTokenSource = null, showLoader = true },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const locations = await API.instance
      .treeSearch(searchTerm, countryIDs, mediaOwnerIDs, networkIDs, cancelTokenSource)
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToGetLocations');
        ErrorHandler.showError(msg, err);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return locations;
  },

  //
  // LINEITEMS
  //
  async getLineItems({ commit }, { queryParams }) {
    if (queryParams.showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const lineitems = await API.instance
      .getLineItems({ queryParams, cancelTokenSource: queryParams.cancelTokenSource })
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToGetLineItems');
        ErrorHandler.showError(msg, err);
        if (queryParams.showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });

    commit(types.SET_LINEITEMS, lineitems.items);
    commit(types.SET_TOTAL_LINEITEMS, lineitems.totalCount);

    if (queryParams.showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return lineitems;
  },
  async getLineItem({ commit }, { lineItemId, cancelTokenSource = null, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const lineitem = await API.instance.getLineItem({ lineItemId, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetLineItem');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    commit(types.SET_LINEITEM, lineitem);

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return lineitem;
  },

  async getEstimatedDeliveryStatistics(_, { params, cancelTokenSource = null }) {
    const estimatedDeliveryStatistics = await API.instance
      .getEstimatedDeliveryStatistics(params, cancelTokenSource)
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToGetEstimatedDeliveryStatistics');
        ErrorHandler.showError(msg, err.message, err.status);
        throw err;
      });
    return estimatedDeliveryStatistics;
  },

  getEstimatedDeliveryStatisticsScreenAndSiteCount({ commit }, { params }) {
    return new Promise((resolve, reject) => {
      API.instance.getEstimatedDeliveryStatisticsScreenAndSiteCount(
        params,
        (result) => {
          resolve(result);
        },
        (error, status) => {
          const msg = i18n.global.t('messages.failedToGetUnitsAndSitesCount');
          ErrorHandler.showError(msg, error, status);
          reject();
        },
      );
    });
  },

  getTargetedUnitAndSiteIDs: createApiAction(
    API.instance.getTargetedUnitAndSiteIDs,
    null,
    'messages.failedToGetUnitsAndSites',
    false,
  ),

  async getCPMForTargets(_, { params, cancelTokenSource = null, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const result = await API.instance.getCPMForTargets({ params, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetLineItemCPM');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return result;
  },

  //
  // MARKETS
  //
  getMarkets: createGetAllAction(API.instance.getMarkets, types.SET_MARKETS, 'messages.failedToGetMarkets', false),

  async getMarketsWithInventory(
    { commit },
    {
      ownerIDs,
      countryIDs,
      networkIDs = null,
      unitTargeting = true,
      excludeGeometry = true,
      cancelTokenSource = null,
      showLoader = true,
    },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const params = {
      owner_ids: ownerIDs.join(','),
      country_ids: countryIDs.join(','),
      network_ids: networkIDs ? networkIDs.join(',') : undefined,
      unit_targeting: unitTargeting,
      exclude_geometry: excludeGeometry,
    };
    const markets = await API.instance.getMarketsWithInventory({ params, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetMarkets');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    commit(types.SET_MARKETS, markets.items);

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return markets;
  },

  //
  // NETWORKS
  //
  getNetworks: createGetAllAction(API.instance.getNetworks, types.SET_NETWORKS, 'messages.failedToGetNetworks', true),

  //
  // MEDIATYPES
  //
  getMediaTypes: createGetAllAction(
    API.instance.getMediaTypes,
    types.SET_MEDIA_TYPES,
    'messages.failedToGetMediaTypes',
    false,
  ),

  //
  // ORGANIZATION DSPS
  //
  async getOrganizationDSPs({ commit }, { showLoader = true } = {}) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const orgDSPs = await API.instance.getOrganizationDSPs().catch((err) => {
      const msg = i18n.global.t('messages.failedToGetOrganizationDSPs');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    commit(types.SET_ORGANIZATION_DSPS, orgDSPs);

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return orgDSPs;
  },

  //
  // PACKAGES
  //
  getPackages: createGetAllAction(
    API.instance.getPackages,
    types.SET_PACKAGES,
    'messages.failedToGetPackages',
    true,
    (commit, packages) => commit(types.SET_TOTAL_PACKAGES, packages.totalCount),
  ),
  getPackage: createGetOneAction(API.instance.getPackage, types.SET_PACKAGE, 'messages.failedToGetPackage', true),
  getPackageAudit: createAuditAction(API.instance.getPackageAudit, 'messages.failedToGetPackageAudit', true),
  putPackageState: createPutAction(
    API.instance.putPackageState,
    types.UPDATE_PACKAGES,
    'messages.failedToUpdatePackage',
    true,
  ),
  createPackage: createPostAction(
    API.instance.postPackages,
    types.UPDATE_PACKAGES,
    'messages.failedToSavePackage',
    true,
  ),
  updatePackage: createPutAction(API.instance.putPackage, types.UPDATE_PACKAGES, 'messages.failedToSavePackage', true),
  deletePackage: createDeleteAction(API.instance.deletePackage, types.DELETE_PACKAGE, 'messages.failedToDeletePackage'),

  async createNewPackage({ commit }, pack = Package.createNew()) {
    commit(types.SET_PACKAGE, pack);
    return pack;
  },

  //
  // SITES
  //
  getSites: createGetAllAction(API.instance.getSites, types.SET_LOCATIONS, 'messages.failedToGetSites', false),

  getSitesBriefUsingPost: createApiAction(API.instance.getSitesBriefUsingPost, null, 'messages.failedToGetSites', true),

  //
  // UNITS
  //
  async getUnits({ commit }, { queryParams, exclusion = false, cancelTokenSource = null, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const units = await API.instance.getUnits({ queryParams, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetUnits');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (exclusion) {
      commit(types.SET_LOCATIONS_EXCLUDED, units.items);
    } else {
      commit(types.SET_LOCATIONS, units.items);
    }

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return units;
  },

  async getGeocodedUnitsAndSitesForTargets(_, { params, cancelTokenSource = null, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const unitsSites = await API.instance.getGeocodedUnitsAndSitesForTargets(params, cancelTokenSource).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetUnitsAndSites');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return unitsSites;
  },

  getFilterChanges: createApiAction(API.instance.getFilterChanges, null, 'messages.failedToGetFilterChanges', false),

  async getAvailableIDsPerFilterType(
    { dispatch, commit },
    { cancelTokenSource = null, showLoader = false, packageID = null, packageGroupID = null, packageIDs = [] } = {},
  ) {
    const filterChanges = await dispatch('getFilterChanges', {
      data: {
        // exchange_id: exchState.exchange.id,
        package_id: packageID,
        packagegroup_id: packageGroupID,
        package_ids: packageIDs,
        // TODO: Activate this when the filter endpoint accepts unitpacks
        filter_types: ['location_id', 'market_id', 'mediatype_id', 'network_id'] /* 'unitpack_id' */,
      },
      cancelTokenSource,
      showLoader,
    });

    const filters = {};
    const keys = Object.keys(filterChanges);
    keys.forEach((key) => {
      filters[key] = filterChanges[key].map((x) => parseInt(x, 10));
    });
    return filters;
  },

  async getClusteredUnitsFromTargets(
    { commit },
    {
      params,
      minLat,
      maxLat,
      minLng,
      maxLng,
      zoom,
      unitIDs,
      cancelTokenSource,
      showLoader = true,
      isPrivate = true,
      planUuid = null,
    },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const filters = await API.instance
      .getUnitFiltersForTargets({ data: params, cancelTokenSource })
      .catch(({ error }) => {
        const msg = i18n.global.t('messages.failedToGetUnits');
        ErrorHandler.showError(msg, error);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw error;
      });

    if (!filters) {
      return null;
    }
    const filterString = filters[0];

    const result = await API.instance
      .getClusteredUnits({
        params: {
          min_lat: minLat,
          max_lat: maxLat,
          min_lon: minLng,
          max_lon: maxLng,
          zoom_level: zoom,
        },
        data: {
          $filter: [filterString],
          ...(unitIDs != null && { unit_ids: unitIDs }),
        },
        cancelTokenSource,
        isPrivate,
        planUuid,
      })
      .catch(({ error }) => {
        const msg = i18n.global.t('messages.failedToGetUnits');
        ErrorHandler.showError(msg, error);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw error;
      });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return result;
  },

  getUnitsInGeofences: createApiAction(API.instance.getUnitsInGeofences, null, 'messages.failedToGetUnits', true),

  getUnitsIDsInGeofences: createApiAction(API.instance.getUnitsIDsInGeofences, null, 'messages.failedToGetUnits', true),

  getUnitsBriefUsingPost: createApiAction(API.instance.getUnitsBriefUsingPost, null, 'messages.failedToGetUnits', true),

  getUnitPacks: createGetAllAction(
    API.instance.getUnitPacks,
    types.SET_UNIT_PACKS,
    'messages.failedToGetUnitPacks',
    true,
  ),
};

export default actions;
